import { forwardRef, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ICONS from "../../../assets/Icons";
import TEXT from "../../../utils/globalText";
import PrimaryButton from "../CommonButtons/PrimaryButton";
import SVGComponent from "../SVGComponent";
import "./index.css";

// -----------------------------------------------------------------------------
// Suggested Props for the DatePicker Component
// -----------------------------------------------------------------------------

/**
 * @typedef {"single" | "range"} SelectionType
 * Represents the selection behavior of the calendar:
 * - "single": allows selecting only one date
 * - "range": enables selecting a date range (start & end)
 */

/**
 * @typedef {"small" | "big"} CalendarSize
 * Controls the visual size and spacing of the calendar UI.
 */

/**
 * Props for the DatePicker component.
 *
 * @param {Object} props
 *
 * @param {Date | null} props.startDate
 * The selected **start date**.
 * - In **single selection** mode, this is the only selected date.
 * - In **range mode**, this represents the starting point of the range.
 *
 * @param {Date | null} [props.endDate]
 * The selected **end date**, only required when using **range** selection.
 * - If not provided, the component behaves as a single-date picker.
 *
 * @param {(date: Date | [Date, Date]) => void} props.handleDateChange
 * A callback function triggered whenever the user selects a date.
 *
 * - In **single selection** mode:
 *   The function receives a **single Date** → `handleDateChange(Date)`
 *
 * - In **range selection** mode:
 *   The function receives an **array containing [startDate, endDate]**
 *   → `handleDateChange([startDate, endDate])`
 *
 * The consumer of this component should use these values to update their own
 * state (e.g., startDate / endDate).
 *
 * @param {Boolean} props.isFullWidthRequired
 *
 * @param {CalendarSize} props.type
 * Determines the calendar size/UI variant.
 * Examples: `"small"` for compact UI, `"big"` for full view.
 *
 * @param {SelectionType} [props.selectionType="single"]
 * Type of date selection:
 * - `"single"` → only one date can be selected
 * - `"range"` → allows selecting a start and end date
 *
 * @param {Date | null} [props.minDate]
 * Minimum selectable date.
 * - Use it to **disable past dates** (e.g., only allow future dates).
 * - If not provided, the calendar supports full date navigation.
 *
 * * @param {Date | null} [props.maxDate]
 * Maximum selectable date.
 * - Use it to **disable future dates**.
 * - If not provided, it will take current date.
 * - If you want future dates to be enabled, pass null in it.
 *
 * @returns {JSX.Element} The rendered DatePicker component.
 */

const CommonCalendar = ({
  isOpen = false,
  startDate,
  endDate,
  handleDateChange,
  type,
  selectionType,
  minDate,
  maxDate = new Date(),
  isFullWidthRequired,
}) => {
  const listRef = useRef(null);
  const isRange = selectionType === "range"; // If endDate exists ---> range
  const today = new Date();
  const effectiveMin = minDate || null; // if minDate passed ---> future only
  const effectiveMax = minDate ? null : maxDate === null ? null : maxDate;
  const datePickerRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const dropdownOptionRef = useRef(null);

  const [dpKey, setDpKey] = useState(0);
  const [currentView, setCurrentView] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(isOpen);
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [hidePrev, setHidePrev] = useState(false);
  const [hideNext, setHideNext] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate || null);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate | null);
  const [yearDropdownTouched, setYearDropdownTouched] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);

  const resolvedOpenDate =
    tempStartDate ||
    localStartDate ||
    (effectiveMin ? effectiveMin : new Date());

  useEffect(() => {
    updateNavigationVisibility(currentYear, currentMonth);
  }, [currentYear, currentMonth, effectiveMin, effectiveMax]);

  const getDayClass = (date) => {
    const currentMonth = new Date(currentView).getMonth();
    const dayMonth = date.getMonth();

    if (dayMonth !== currentMonth) {
      return "datepicker-day-faded"; // Past or future month → faded
    }

    return ""; // Normal days
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current?.contains(event.target)
      ) {
        setShowDatePicker(false);
        setOpenDropdown(null);
        setYearDropdownTouched(false); // user interacted with the dropdown
        setCurrentYear(today.getFullYear());
        // FORCE REMOUNT
        setDpKey((prev) => prev + 1);
        if (!startDate) {
          if (effectiveMin) {
            setCurrentView(effectiveMin);
            setCurrentMonth(effectiveMin.getMonth());
            setCurrentYear(effectiveMin.getFullYear());
          } else {
            resetToToday();
          }
        }
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate || null);
    setLocalStartDate(startDate);
    setLocalEndDate(endDate || null);

    const refDate = endDate || startDate;
    if (refDate instanceof Date && !Number.isNaN(refDate.getTime())) {
      setCurrentView(refDate);
      setCurrentMonth(refDate.getMonth());
      setCurrentYear(refDate.getFullYear());
      updateNavigationVisibility(refDate.getFullYear(), refDate.getMonth());
    }
  }, [startDate, endDate]);

  useEffect(() => {
    setTempStartDate(localStartDate);
    setTempEndDate(localEndDate);
  }, [showDatePicker]);

  useEffect(() => {
    if (openDropdown === null) {
      setYearDropdownTouched(false);
    }
  }, [openDropdown]);

  const updateNavigationVisibility = (year, month) => {
    let _hidePrev = false;
    let _hideNext = false;

    if (effectiveMin) {
      const minYear = effectiveMin.getFullYear();
      const minMonth = effectiveMin.getMonth();
      if (year < minYear || (year === minYear && month <= minMonth)) {
        _hidePrev = true;
      }
    }

    if (effectiveMax) {
      const maxYear = effectiveMax.getFullYear();
      const maxMonth = effectiveMax.getMonth();
      if (year > maxYear || (year === maxYear && month >= maxMonth)) {
        _hideNext = true;
      }
    }

    setHidePrev(_hidePrev);
    setHideNext(_hideNext);
  };

  const resetToStartDate = () => {
    if (!localStartDate) return;

    const date = new Date(localStartDate);

    setCurrentYear(date.getFullYear());
    setCurrentMonth(date.getMonth());
    setCurrentView(date);
  };

  const resetToToday = () => {
    const today = new Date();
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
    setCurrentView(today);
  };

  const handleMonthChange = (date) => {
    const newMonth = date.getMonth();
    const newYear = date.getFullYear();
    setCurrentView(date);
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    updateNavigationVisibility(newYear, newMonth);
  };

  const handleChange = (dates) => {
    if (isRange) {
      const [start, end] = dates;
      setTempStartDate(start);
      setTempEndDate(end || null);
    } else {
      setTempStartDate(dates);
    }
  };

  const handleConfirm = () => {
    if (showDatePicker) {
      setShowDatePicker(false);
      setOpenDropdown(null);

      // Update real local state only here
      setLocalStartDate(tempStartDate);
      setLocalEndDate(tempEndDate);

      // Send to parent
      if (isRange) {
        handleDateChange([tempStartDate, tempEndDate]);
      } else {
        handleDateChange(tempStartDate);
      }
    }
  };

  const handleClear = () => {
    if (showDatePicker) {
      setOpenDropdown(null);
      setYearDropdownTouched(false); // user interacted with the dropdown

      if (isRange) {
        setTempStartDate(null);
        setTempEndDate(null);

        setLocalStartDate(null);
        setLocalEndDate(null);

        handleDateChange([null, null]);
      } else {
        setTempStartDate(null);
        handleDateChange(null);
        setLocalStartDate(null);
      }

      // Reset calendar view to today's date
      resetToToday();

      // FORCE REMOUNT
      setDpKey((prev) => prev + 1);

      setShowDatePicker(false);
    }
  };

  const formatDate = (date) => {
    return `${date.getDate()} ${date.toLocaleString("default", {
      month: "short",
    })}, ${date.getFullYear()}`;
  };

  const Dropdown = forwardRef(
    (
      {
        year, // current year value
        options, // array of { name: "2023", value: 2023 }
        selectedOption,
        setSelectedOption,
        isOpen,
        dropdownKey,
        setOpenDropdown,
        dropdownRef,
      },
      ref,
    ) => {
      useEffect(() => {
        if (isOpen && listRef.current) {
          const selectedEl = listRef.current.querySelector(
            `[data-value="${selectedOption?.value}"]`,
          );

          if (selectedEl) {
            listRef.current.scrollTop = selectedEl.offsetTop - 10;
          }
        }
      }, [isOpen, selectedOption]);

      useEffect(() => {
        const handleClickOutside = (event) => {
          if (
            dropdownRef.current &&
            !dropdownRef.current?.contains(event.target)
          ) {
            setOpenDropdown(null);
            setYearDropdownTouched(false); // user interacted with the dropdown
          }
        };
        document.addEventListener("click", handleClickOutside);
        return () => {
          document.removeEventListener("click", handleClickOutside);
        };
      }, []);
      return (
        <div
          ref={dropdownRef}
          className={`flex flex-col relative w-year-dropdown-width flex-[1] bg-primary-nav ${!isOpen ? "rounded-border-radius-md " : " rounded-b-none rounded-t-border-radius-md"}`}
        >
          {/* Header */}
          <div
            className={`flex flex-col ${!isOpen ? "global-hover-white relative z-z-index-content rounded-border-radius-md " : " rounded-b-none"}`}
          >
            <div
              className={`flex justify-between items-center py-padding-sm cursor-pointer border-b-border ${
                isOpen
                  ? "border-b-primary-bg mx-margin-md "
                  : "px-padding-md w-width-full border-b-[transparent]"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setYearDropdownTouched(true); // user interacted with the dropdown
                setOpenDropdown(isOpen ? null : dropdownKey);
              }}
            >
              {/* Selected year text */}
              <div className="flex items-center">
                <div
                  className={`text-font-xs text-text-primary whitespace-nowrap overflow-ellipsis overflow-hidden w-width-full`}
                >
                  {selectedOption?.name}
                </div>
              </div>

              {/* Arrow Icons */}
              <div className="flex gap-gap-sm justify-between h-[-webkit-fill-available]">
                <div className="flex items-center h-height-full">
                  <SVGComponent
                    url={
                      isOpen
                        ? "https://q2pay.in/new_assets/common/arrows/arrow_up_white.svg"
                        : "https://q2pay.in/new_assets/common/arrows/arrow_down_white.svg"
                    }
                    color={"var(--text-primary)"}
                    height={"var(--icon-xs)"}
                    width={"var(--icon-xs)"}
                    alt="Arrow"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Dropdown List */}
          <div className="w-width-full absolute top-top-full bg-primary-nav rounded-b-border-radius-md z-z-index-tooltip ">
            <div
              ref={listRef}
              className={`overflow-y-auto ${
                isOpen
                  ? yearDropdownTouched
                    ? "calendarYearDropdown-open"
                    : "h-height-base"
                  : yearDropdownTouched
                    ? "calendarYearDropdown-close"
                    : "h-height-base"
              }`}
            >
              <div className="flex flex-col gap-gap-sm pt-padding-sm pb-padding-xs px-padding-sm">
                {options.map((val) => {
                  const isSelected = selectedOption?.value === val.value;

                  return (
                    <div
                      key={val.value}
                      data-value={val.value}
                      className={`flex justify-between items-center px-padding-sm py-padding-button-s relative z-z-index-header rounded-border-radius-md cursor-pointer w-width-full global-hover-white ${
                        isSelected ? "bg-primary-card" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOption({
                          name: val?.name,
                          value: val?.value,
                        });
                        setCurrentYear(val?.value);
                        setOpenDropdown(null);
                      }}
                    >
                      <div className="flex gap-gap-xs justify-between items-center w-auto">
                        <div
                          className={`text-font-xs text-text-primary whitespace-nowrap overflow-ellipsis overflow-hidden w-width-full`}
                        >
                          {val.name}
                        </div>
                      </div>

                      {/* Checkmark */}
                      {isSelected ? (
                        <SVGComponent
                          url={
                            "https://q2pay.in/new_assets/common/view_all/check_mark_white.svg"
                          }
                          color={"var(--text-primary)"}
                          height={"var(--icon-xs)"}
                          width={"var(--icon-xs)"}
                          alt="Arrow"
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {isOpen ? (
              <div className="pb-padding-sm pt-padding-sm w-width-full rounded-b-border-radius-sm"></div>
            ) : null}
          </div>
        </div>
      );
    },
  );

  const calenderHeight = 330;

  return (
    <div
      className={` relative h-auto flex flex-col gap-gap-sm w-width-full ${isFullWidthRequired ? "w-width-full" : type === "big" ? "Qsmd:max-w-calendar-width-big" : "Qsmd:max-w-calendar-select-width-small"} `}
      ref={datePickerRef}
    >
      <div
        className={`flex justify-between items-center gap-gap-sm rounded-border-radius-md py-padding-sm px-padding-md bg-primary-nav-act w-width-full cursor-pointer global-hover-white relative z-z-index-header ${isFullWidthRequired ? "w-width-full" : type === "big" ? "Qsmd:max-w-calendar-width-big" : "Qsmd:max-w-calendar-select-width-small"} `}
        onClick={() => {
          const containerRect = document
            .getElementById("root")
            ?.getBoundingClientRect();
          const cardRect = dropdownOptionRef?.current?.getBoundingClientRect();
          const willOverflowBottom =
            cardRect?.bottom > containerRect?.bottom &&
            cardRect?.top >= calenderHeight;
          setOpenUpward(willOverflowBottom);
          setShowDatePicker(!showDatePicker);
          setYearDropdownTouched(false); // user interacted with the dropdown
          setOpenDropdown(null);
          setTempStartDate(startDate);
          setTempEndDate(endDate || null);
          // setLocalStartDate(startDate);
          // setLocalEndDate(endDate || null);
          resetToStartDate();
          if (!localStartDate) setCurrentYear(today.getFullYear());
          // FORCE REMOUNT
          setDpKey((prev) => prev + 1);
          if (!startDate) {
            if (effectiveMin) {
              setCurrentView(effectiveMin);
              setCurrentMonth(effectiveMin.getMonth());
              setCurrentYear(effectiveMin.getFullYear());
            } else {
              resetToToday();
            }
          }
        }}
      >
        <div
          className={`${type === "big" ? "text-font-md" : "text-font-xs"} text-text-secondary flex-1`}
        >
          {isRange
            ? (localStartDate && formatDate(localStartDate)) || TEXT.FROM_DATE
            : (localStartDate && formatDate(localStartDate)) ||
              TEXT.SELECT_DATE}
        </div>

        {!isRange && (
          <div
            className={`transition-transform duration-animation-fast ease-in-out ${
              showDatePicker ? "rotate-half" : "rotate-base"
            }`}
          >
            <SVGComponent
              url={ICONS?.ARROW_DOWN_WHITE}
              color="var(--text-primary)"
              width="var(--icon-xs)"
              height="var(--icon-xs)"
            />
          </div>
        )}

        {isRange ? (
          <>
            <SVGComponent
              url="https://q2pay.in/new_assets/common/arrows/arrow_right_grey.svg"
              width={"var(--icon-sm)"}
              height={"var(--icon-sm)"}
              alt={"Arrow Right Grey"}
              color={"var(--text-primary)"}
            />
            <div
              className={`${type === "big" ? "text-font-md" : "text-font-xs"} text-text-secondary flex flex-1 justify-end`}
            >
              {(localEndDate && formatDate(localEndDate)) || TEXT.TO_DATE}
            </div>
          </>
        ) : null}
      </div>
      <div
        ref={dropdownOptionRef}
        className={`absolute w-width-full ${openUpward ? "bottom-[calc(var(--top-full)+var(--padding-sm))]" : " top-[calc(var(--top-full)+var(--padding-sm))]"} shadow-box-shadow-all z-z-index-dropdown transition-[max-height,opacity] duration-animation-fast-s ease-in-out rounded-border-radius-md ${showDatePicker ? "max-h-max-height-for-calendar-animation opacity-base-opacity" : "max-h-[var(--height-base)] opacity-opacity-none"}`}
      >
        <div
          className={`relative flex-col bg-primary-nav-act rounded-border-radius-md p-padding-md w-width-full shadow-box-shadow-all ${showDatePicker ? "flex" : "hidden"}  ${type === "big" ? "calendar-big" : "calendar-small"}`}
        >
          <DatePicker
            key={dpKey}
            inline
            // showYearDropdown
            focusSelectedMonth={true}
            minDate={effectiveMin}
            maxDate={effectiveMax}
            openToDate={resolvedOpenDate}
            onMonthChange={handleMonthChange}
            // RANGE MODE
            selected={tempStartDate}
            selectsRange={isRange}
            startDate={tempStartDate}
            endDate={isRange ? tempEndDate : null}
            onChange={(date) => handleChange(date)}
            dayClassName={(date) => getDayClass(date)}
            renderCustomHeader={({
              date,
              decreaseMonth,
              increaseMonth,
              changeYear,
            }) => {
              const year = currentYear;

              const monthName = date.toLocaleString("default", {
                month: "long",
              });

              return (
                <div className="flex flex-col items-center gap-gap-sm pb-padding-sm border-b-border border-b-primary-bg">
                  {/* Top Row: Arrows + Month */}
                  <div className="flex justify-between items-center w-width-full p-padding-xs">
                    <button
                      onClick={!hidePrev ? decreaseMonth : undefined}
                      style={{
                        opacity: hidePrev
                          ? "var(--light-opacity)"
                          : "var(--base-opacity)",
                      }}
                      className="rotate-180"
                    >
                      <SVGComponent
                        url="https://q2pay.in/new_assets/common/arrows/arrow_right_grey.svg"
                        width={"var(--icon-sm)"}
                        height={"var(--icon-sm)"}
                        alt={"Arrow Left"}
                        color={"var(--text-primary)"}
                      />
                    </button>
                    <span className="text-text-primary text-font-xs">
                      {monthName}
                    </span>
                    <button
                      onClick={!hideNext ? increaseMonth : undefined}
                      style={{
                        opacity: hideNext
                          ? "var(--light-opacity)"
                          : "var(--base-opacity)",
                      }}
                    >
                      <SVGComponent
                        url="https://q2pay.in/new_assets/common/arrows/arrow_right_grey.svg"
                        width={"var(--icon-sm)"}
                        height={"var(--icon-sm)"}
                        alt={"Arrow Right"}
                        color={"var(--text-primary)"}
                      />
                    </button>
                  </div>
                  {/* YEAR DROPDOWN */}
                  <Dropdown
                    year={year}
                    options={[
                      ...(() => {
                        const today = new Date();
                        const currentYear = today.getFullYear();

                        let years = [];

                        if (effectiveMin) {
                          // CASE 1: minDate given, ascending from minYear, minYear + 99
                          const minYear = effectiveMin.getFullYear();
                          const maxAllowed = Math.min(
                            minYear + 100,
                            effectiveMax
                              ? effectiveMax.getFullYear()
                              : minYear + 100,
                          );

                          years = Array.from(
                            { length: maxAllowed - minYear + 1 },
                            (_, i) => minYear + i,
                          );
                        } else if (maxDate === null) {
                          // CASE 2: no maxDate → show past 100 and future 100 years
                          const centerYear = effectiveMax
                            ? effectiveMax.getFullYear()
                            : currentYear;

                          const startYear = centerYear - 100;
                          const endYear = centerYear + 100;

                          years = Array.from(
                            { length: endYear - startYear + 1 },
                            (_, i) => startYear + i,
                          );
                        } else {
                          // CASE 3: no minDate, last 100 years (descending)
                          const maxYear = Math.min(
                            currentYear,
                            effectiveMax
                              ? effectiveMax.getFullYear()
                              : currentYear,
                          );

                          years = Array.from(
                            { length: 101 },
                            (_, i) => maxYear - i,
                          );
                        }

                        return years.map((y) => ({
                          name: y,
                          value: y,
                        }));
                      })(),
                    ]}
                    selectedOption={{
                      name: year,
                      value: year,
                    }}
                    setSelectedOption={(opt) => changeYear(opt.value)}
                    isOpen={openDropdown === "year"}
                    dropdownKey="year"
                    setOpenDropdown={setOpenDropdown}
                    dropdownRef={yearDropdownRef}
                  />
                </div>
              );
            }}
            renderDayContents={(day, date) => {
              const formattedDay = String(day).padStart(2, "0");
              return <span>{formattedDay}</span>;
            }}
          />

          <div className="w-width-full flex gap-gap-md">
            <PrimaryButton
              btnText={TEXT?.CLEAR}
              handleFunction={handleClear}
              enabledTextColor="var(--text-primary)"
              disabledTextColor="var(--text-primary)"
              enabledBgColor="var(--primary-nav-act)"
              disabledBgColor="var(--primary-nav-act)"
              outLineColor="var(--text-primary)"
              hoverOption="white"
              isDisabled={!tempStartDate}
              fullWidthRequired
            />

            <PrimaryButton
              btnText={TEXT?.CONFIRM}
              handleFunction={handleConfirm}
              enabledTextColor="var(--primary-bg)"
              disabledTextColor="var(--text-primary)"
              enabledBgColor="var(--text-primary)"
              disabledBgColor="var(--text-disabled)"
              hoverOption="black"
              isDisabled={
                isRange ? !tempStartDate || !tempEndDate : !tempStartDate
              }
              fullWidthRequired
              inActiveVariantNeeded
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonCalendar;
